import {StateCreator} from "zustand/vanilla"
import {Tag} from "@/pb/api/devplan/types/tags"
import {GetTagsResponse, CreateTagRequest, CreateTagResponse, UpdateTagRequest, UpdateTagResponse, DeleteTagRequest} from "@/pb/api/devplan/services/web/company"
import {tagsAPI} from "@/app/apiPaths"
import {jr} from "@/hooks/queries/response-handler"

export type TagsStoreState = {
  tags: Record<number, Tag[]>
  fetchTags(companyId: number): Promise<void>
  createTag(companyId: number, tag: Tag): Promise<Tag>
  updateTag(companyId: number, tag: Tag): Promise<Tag>
  deleteTag(companyId: number, tagId: string): Promise<void>
}

export const createTagsSlice: StateCreator<
  TagsStoreState,
  [],
  [],
  TagsStoreState
> = ((set, get) => ({
  tags: {},
  fetchTags: async (companyId) => {
    const response = await fetch(tagsAPI(companyId))
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    const js = await response.json()
    const {tags} = GetTagsResponse.fromJSON(js)
    const sortedTags = (tags || []).sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    set(s => ({
      ...s,
      tags: {...s.tags, [companyId]: sortedTags},
    }))
  },
  createTag: async (companyId, tag) => {
    const js = await fetch(tagsAPI(companyId), {
      method: "POST",
      body: JSON.stringify(CreateTagRequest.toJSON({tag}))
    }).then(jr)
    const {tag: created} = CreateTagResponse.fromJSON(js)
    set(s => {
      const companyTags = s.tags[companyId] || []
      const updatedTags = [...companyTags, created!].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
      return {
        ...s,
        tags: {...s.tags, [companyId]: updatedTags}
      }
    })
    return created!
  },
  updateTag: async (companyId, tag) => {
    const js = await fetch(tagsAPI(companyId), {
      method: "PUT",
      body: JSON.stringify(UpdateTagRequest.toJSON({tag}))
    }).then(jr)
    const {tag: updated} = UpdateTagResponse.fromJSON(js)
    set(s => {
      const companyTags = s.tags[companyId] || []
      const existingIndex = companyTags.findIndex(t => t.id === updated!.id)
      const updatedTags = existingIndex >= 0
        ? companyTags.map((t, i) => i === existingIndex ? updated! : t)
        : [...companyTags, updated!]
      const sortedTags = updatedTags.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
      return {
        ...s,
        tags: {...s.tags, [companyId]: sortedTags}
      }
    })
    return updated!
  },
  deleteTag: async (companyId, tagId) => {
    const response = await fetch(tagsAPI(companyId), {
      method: "DELETE",
      body: JSON.stringify(DeleteTagRequest.toJSON({tagId}))
    })
    if (!response.ok) {
      // Try to extract error message from response body
      try {
        const errorData = await response.json()
        const errorMessage = errorData.error || response.statusText
        throw new Error(errorMessage)
      } catch (e) {
        if (e instanceof Error && e.message) {
          throw e
        }
        throw new Error(response.statusText || 'Failed to delete tag')
      }
    }
    // Update store only on success
    set(s => ({
      ...s,
      tags: {...s.tags, [companyId]: (s.tags[companyId] || []).filter(t => t.id !== tagId)}
    }))
  }
}))
